import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-regular-svg-icons';
import { faLoader } from '@fortawesome/pro-solid-svg-icons';

const FurnitureInventorySign = () => {
    const { linkUrl } = useParams();
    const history = useHistory();
    const signatureRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [data, setData] = useState(null);
    const [signed, setSigned] = useState(false);

    useEffect(() => {
        loadInventory();
    }, [linkUrl]);

    const loadInventory = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}furniture-inventory/sign/${linkUrl}`);
            
            if (response.data.success) {
                setData(response.data);
                setSigned(response.data.signed || false);
            } else {
                setError(response.data.message || 'Failed to load inventory');
            }
        } catch (err) {
            console.error('Error loading inventory:', err);
            setError(err.response?.data?.message || 'Invalid or expired link');
        } finally {
            setLoading(false);
        }
    };

    const clearSignature = () => {
        if (signatureRef.current) {
            signatureRef.current.clear();
        }
    };

    const handleSubmit = async () => {
        if (!signatureRef.current || signatureRef.current.isEmpty()) {
            alert('Please provide your signature before submitting');
            return;
        }

        try {
            setSubmitting(true);
            const signatureData = signatureRef.current.toDataURL();

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}furniture-inventory/sign/${linkUrl}`,
                { signature: signatureData }
            );

            if (response.data.success) {
                setSuccess(true);
                setSigned(true);
            } else {
                setError(response.data.message || 'Failed to submit signature');
            }
        } catch (err) {
            console.error('Error submitting signature:', err);
            setError(err.response?.data?.message || 'Failed to submit signature');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FontAwesomeIcon icon={faLoader} className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading furniture inventory...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                    <FontAwesomeIcon icon={faTimesCircle} className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => history.push('/')}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    if (success || signed) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                    <FontAwesomeIcon icon={faCheckCircle} className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        {success ? 'Thank You!' : 'Already Signed'}
                    </h2>
                    <p className="text-gray-600 mb-6">
                        {success 
                            ? 'Your furniture inventory checklist has been signed successfully.'
                            : 'This inventory checklist has already been signed.'}
                    </p>
                    {data?.signature?.signed_at && (
                        <p className="text-sm text-gray-500">
                            Signed on: {new Date(data.signature.signed_at).toLocaleString()}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const items = data?.items || [];
    const booking = data?.booking;
    const user = data?.user;

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="bg-blue-600 text-white p-6">
                        <h1 className="text-3xl font-bold mb-2">Furniture Inventory Checklist</h1>
                        <p className="text-blue-100">
                            Please review the furniture items in your unit and sign below to confirm receipt.
                        </p>
                    </div>

                    {/* Booking Info */}
                    {booking && (
                        <div className="border-b border-gray-200 p-6 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600">Tenant Name</p>
                                    <p className="font-semibold text-gray-900">
                                        {user?.name} {user?.surname}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Booking Reference</p>
                                    <p className="font-semibold text-gray-900">{booking.booking_reference}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Property</p>
                                    <p className="font-semibold text-gray-900">{booking.property?.name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Unit</p>
                                    <p className="font-semibold text-gray-900">{booking.unit?.name}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Inventory Items */}
                    <div className="p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Furniture & Equipment</h2>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b-2 border-gray-300">
                                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Item</th>
                                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => (
                                        <tr key={item.id || index} className="border-b border-gray-200 hover:bg-gray-50">
                                            <td className="py-3 px-4 text-gray-900">{item.item_name}</td>
                                            <td className="py-3 px-4 text-center text-gray-900 font-semibold">
                                                {item.quantity}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {items.length === 0 && (
                            <p className="text-center text-gray-500 py-8">No inventory items found</p>
                        )}
                    </div>

                    {/* Signature Section */}
                    <div className="border-t border-gray-200 p-6 bg-gray-50">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Signature</h2>
                        <p className="text-sm text-gray-600 mb-4">
                            By signing below, I confirm that I have received the furniture and equipment listed above
                            in good condition.
                        </p>

                        <div className="bg-white border-2 border-gray-300 rounded-lg mb-4">
                            <SignatureCanvas
                                ref={signatureRef}
                                canvasProps={{
                                    className: 'w-full h-48 cursor-crosshair',
                                }}
                                backgroundColor="white"
                            />
                        </div>

                        <div className="flex gap-4">
                            <button
                                onClick={clearSignature}
                                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
                                disabled={submitting}
                            >
                                Clear Signature
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <FontAwesomeIcon icon={faLoader} className="w-5 h-5 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit Signature'
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-6 text-sm text-gray-500">
                    <p>If you have any questions, please contact your property manager.</p>
                </div>
            </div>
        </div>
    );
};

export default FurnitureInventorySign;
